import { useState } from "react";
import useSWR from "swr";
import { Expand } from "lucide-react";
import { photoUrl } from "@/lib/api";
import { Card, SectionHeader } from "@/components/common/Ornament";
import { RichHtml } from "@/components/common/RichText";
import Lightbox from "@/components/property/Lightbox";
import LocationMap from "@/components/property/LocationMap";
import MediaEmbed from "@/components/property/MediaEmbed";

function Gallery({ photos, onOpen }) {
  if (!photos.length) return <p className="text-sm text-slate-500" data-testid="gallery-empty">No photos yet.</p>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="gallery-grid">
      {photos.map((p, i) => (
        <button key={p.id} onClick={() => onOpen(i)} data-testid={`gallery-item-${i}`}
          className={`group relative overflow-hidden rounded-lg bg-[#0F2E23] ${i === 0 ? "col-span-2 row-span-2 aspect-[4/3] md:aspect-auto" : "aspect-[4/3]"}`}>
          <img src={photoUrl(p.id)} alt={p.caption} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0F2E23]/90 to-transparent p-3 text-left">
            <p className="text-xs sm:text-sm text-[#F9F6F0] font-medium line-clamp-2">{p.caption}</p>
          </div>
          <Expand className="absolute right-3 top-3 h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      ))}
    </div>
  );
}

function Plans({ plans, onOpen }) {
  const [sel, setSel] = useState(0);
  if (!plans.length) return <p className="text-sm text-slate-500">No plans uploaded yet.</p>;
  const cur = plans[Math.min(sel, plans.length - 1)];
  return (
    <div data-testid="floorplan-viewer">
      <div className="flex gap-2 overflow-x-auto pb-3">
        {plans.map((p, i) => (
          <button key={p.id} onClick={() => setSel(i)} data-testid={`floorplan-tab-${i}`}
            className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${i === sel ? "bg-[#0F2E23] text-[#F9F6F0] border-[#0F2E23]" : "bg-white border-[#E2DCD2] text-[#3E5C4E] hover:border-[#C59B27]"}`}>
            {p.caption}
          </button>
        ))}
      </div>
      <button onClick={() => onOpen(sel)} className="relative block w-full rounded-lg border border-[#E2DCD2] bg-white overflow-hidden group" data-testid="floorplan-main">
        <img src={photoUrl(cur.id)} alt={cur.caption} className="w-full max-h-[620px] object-contain" />
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded bg-[#0F2E23]/85 px-2 py-1 text-xs text-[#F9F6F0]"><Expand className="h-3 w-3" /> Full screen</span>
      </button>
    </div>
  );
}

export default function Property() {
  const { data: c } = useSWR("/content");
  const { data: photos = [] } = useSWR("/photos");
  const [box, setBox] = useState(null);
  const gallery = photos.filter((p) => p.category === "gallery");
  const plans = photos.filter((p) => p.category === "plan");
  const items = (list) => list.map((p) => ({ src: photoUrl(p.id), caption: p.caption }));

  return (
    <div className="space-y-10">
      <SectionHeader eyebrow="The Property" title="Site, Design & Location" testId="property-header">{c?.address}</SectionHeader>
      <Gallery photos={gallery} onOpen={(i) => setBox({ list: items(gallery), i })} />
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-5" eyebrow="Program" title="About the Hotel" testId="property-description">
          <RichHtml html={c?.property_html} />
        </Card>
        <Card className="lg:col-span-7" eyebrow="Location" title="Demand Drivers" testId="property-map-card">
          {c && <LocationMap subject={c.map.subject} points={c.map.attractions} testId="leaflet-property-map" />}
          <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
            {c?.map.attractions.map((a, i) => (
              <div key={i} className="flex gap-2" data-testid={`attraction-${i}`}>
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#3E5C4E] shrink-0" />
                <span><strong className="text-[#0F2E23]">{a.name}</strong>{a.description && <span className="text-slate-500"> — {a.description}</span>}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Card eyebrow="Drawings" title="Site Plans, Renderings & Floor Plans" testId="property-plans">
        <Plans plans={plans} onOpen={(i) => setBox({ list: items(plans), i })} />
        <p className="text-xs text-slate-500 mt-3">The full conceptual design package is available in Documents.</p>
      </Card>
      <Card eyebrow="Media" title="Video & Virtual Tour" testId="property-media">
        <MediaEmbed videoUrl={c?.video_url} tourUrl={c?.tour_url} />
      </Card>
      {box && <Lightbox items={box.list} index={box.i} onClose={() => setBox(null)} onIndex={(i) => setBox((b) => ({ ...b, i }))} />}
    </div>
  );
}
